import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { AdministratorsService } from '../administrations/administrators-service';
import { Shape } from 'src/app/models/configurators-2d/applications/decking-boards/shape';
import { LayingType } from 'src/app/models/configurators-2d/applications/decking-boards/laying-type';
import { DeckingBoardType } from 'src/app/models/configurators-2d/applications/decking-boards/decking-board-type';
import { DeckingBoard } from 'src/app/models/configurators-2d/applications/decking-boards/decking-board';
import { DeckingBoardFinishingType } from 'src/app/models/configurators-2d/applications/decking-boards/decking-board-finishing-type';
import { DeckingBoardFinishing } from 'src/app/models/configurators-2d/applications/decking-boards/decking-board-finishing';
import { Supply } from 'src/app/models/configurators-2d/applications/decking-boards/supply';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { BaseAppService } from '../base-app-service';
import { Configuration } from 'src/app/models/configurators-2d/applications/decking-boards/configuration';
import { Pedestal } from 'src/app/models/configurators-2d/applications/decking-boards/pedestal';
import { Information2D } from 'src/app/models/configurators-2d/information2d';
import { Joist } from 'src/app/models/configurators-2d/applications/decking-boards/joist';
import { FixationGroup } from 'src/app/models/configurators-2d/applications/decking-boards/fixation-group';
import { Accessory } from 'src/app/models/configurators-2d/applications/decking-boards/accessory';
import { JoistType } from 'src/app/enum/decking-boards/joist-type.enum';

@Injectable({
  providedIn: 'root',
})
export class DeckingBoardsService extends BaseAppService<Configuration> {
  constructor(protected administratorsService: AdministratorsService) {
    super(CONFIG_CONTEXT.DECKING_BOARDS, Configuration);
  }

  getShapes(): Promise<Shape[]> {
    return this.get<Shape[]>(this.apiUrl + 'Configuration/shapes');
  }

  getLayingTypes(): Promise<LayingType[]> {
    return this.get<LayingType[]>(this.apiUrl + 'Configuration/layingTypes');
  }

  getDeckingBoardTypes(): Promise<DeckingBoardType[]> {
    return this.get<DeckingBoardType[]>(this.apiUrl + 'Configuration/deckingBoardTypes');
  }

  getDeckingBoards(): Promise<DeckingBoard[]> {
    const params = new HttpParams().set('requireFinishings', true);
    return this.get<DeckingBoard[]>(this.apiUrl + 'Configuration/deckingBoards', params);
  }

  getDeckingBoardFinishings(): Promise<DeckingBoardFinishing[]> {
    return this.get<DeckingBoardFinishing[]>(this.apiUrl + 'Configuration/deckingBoardFinishings');
  }

  getDeckingBoardFinishingTypes(): Promise<DeckingBoardFinishingType[]> {
    return this.get<DeckingBoardFinishingType[]>(this.apiUrl + 'Configuration/deckingBoardFinishingTypes');
  }

  getJoists(): Promise<Joist[]> {
    return this.get<Joist[]>(this.apiUrl + 'Configuration/joists');
  }

  getFixationGroups(): Promise<FixationGroup[]> {
    return this.get<FixationGroup[]>(this.apiUrl + 'Configuration/fixationGroups');
  }

  getSupplies(): Promise<Supply[]> {
    return this.get<Supply[]>(this.apiUrl + 'Configuration/supplies');
  }

  getAccessories(joisType: JoistType, locationId: number, deckingBoardTypeId: number): Promise<Accessory[]> {
    const params = new HttpParams()
      .set('joistType', joisType)
      .set('locationId', locationId)
      .set('deckingBoardTypeId', deckingBoardTypeId);
    return this.get<Accessory[]>(this.apiUrl + 'Configuration/accessories', params);
  }

  getPedestals(): Promise<Pedestal[]> {
    return this.get<Pedestal[]>(this.apiUrl + 'Configuration/pedestals');
  }

  getClassesInformations(): Promise<Information2D[]> {
    return Promise.resolve([
      {
        titleFr: 'Classe 1',
        titleEn: 'Class 1',
        subTitleFr: 'Bois toujours sec. Humidité inférieure à 20 %.',
        subTitleEn: 'Always dry wood. Humidity below 20%',
        descriptionFr: 'Intérieur uniquement: meuble, parquet, lambris, menuiserie',
        descriptionEn: 'Interior only: furniture, parquet, paneling, carpentry',
      },
      {
        titleFr: 'Classe 2',
        titleEn: 'Class 2',
        subTitleFr: 'Bois sec, soumis occasionnelement à une humidité supérieure à 20%',
        subTitleEn: 'Dry wood, occasionally subjected to humidity above 20%',
        descriptionFr: 'Charpente et ossature correctement ventilées',
        descriptionEn: 'Properly ventilated frame and framework',
      },
      {
        titleFr: 'Classe 3',
        titleEn: 'Class 3',
        subTitleFr: 'Bois soumis à une humidité fréquemment supérieure à 20%',
        subTitleEn: 'Wood subjected to humidity frequently above 20%',
        descriptionFr: 'Toute pièce de construction ou menuiserie extérieure soumise à la pluie: bardage, fenêtre...',
        descriptionEn: 'Any piece of construction or exterior joinery subject to rain: cladding, window...',
      },
      {
        titleFr: 'Classe 4',
        titleEn: 'Class 4',
        subTitleFr: 'Bois soumis à une humidité toujours supérieure à 20%',
        subTitleEn: 'Wood subject to humidity always above 20%',
        descriptionFr:
          "Bois horizontal en extérieure et bois en contaxt avec le sol ou source d'humidification prolongée",
        descriptionEn:
          'Horizontal wood outdoors and wood in contact with the ground or source of prolonged humidification',
      },
      {
        titleFr: 'Classe 5',
        titleEn: 'Class 5',
        subTitleFr: "Bois en contact avec de l'eau de mer",
        subTitleEn: 'Wood in contact with sea water',
        descriptionFr: 'Pilier, ponton, bois immergé',
        descriptionEn: 'Pillar, pontoon, submerged timber',
      },
    ]);
  }
}
